import React from 'react';
import { Snackbar, Alert, Box, Typography, LinearProgress, Chip } from '@mui/material';
import { Wifi, WifiOff, CloudQueue, CloudDone, Storage } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import offlineRequestQueue from '../../utils/offlineRequestQueue';

/**
 * Network status indicator for PWA
 * Shows when user goes offline/online, cached content availability, and queued requests
 */
const NetworkStatusIndicator = () => {
  const { t } = useTranslation('common');
  const { isOnline } = useNetworkStatus();
  const [showOfflineAlert, setShowOfflineAlert] = React.useState(false);
  const [showOnlineAlert, setShowOnlineAlert] = React.useState(false);
  const [queueSize, setQueueSize] = React.useState(0);
  const [cachedPagesCount, setCachedPagesCount] = React.useState(0);
  const [isProcessingQueue, setIsProcessingQueue] = React.useState(false);

  // Check cached content availability
  React.useEffect(() => {
    const checkCachedContent = async () => {
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          let totalCached = 0;
          
          for (const cacheName of cacheNames) {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();
            totalCached += keys.length;
          }
          
          setCachedPagesCount(totalCached);
        } catch (error) {
          console.error('Failed to check cached content:', error);
        }
      }
    };
    
    checkCachedContent();
  }, []);

  // Listen for queue changes
  React.useEffect(() => {
    const handleQueueChange = (size) => {
      setQueueSize(size);
    };
    
    offlineRequestQueue.addListener(handleQueueChange);
    setQueueSize(offlineRequestQueue.getQueueSize());
    
    return () => {
      offlineRequestQueue.removeListener(handleQueueChange);
    };
  }, []);

  // Handle online/offline transitions
  React.useEffect(() => {
    if (!isOnline) {
      setShowOfflineAlert(true);
      setShowOnlineAlert(false);
    } else {
      setShowOfflineAlert(false);
      
      // Process queued requests when back online
      if (queueSize > 0) {
        setIsProcessingQueue(true);
        offlineRequestQueue.processQueue().finally(() => {
          setIsProcessingQueue(false);
        });
      }
      
      if (showOfflineAlert) {
        setShowOnlineAlert(true);
        // Auto-hide online notification after 3s
        setTimeout(() => setShowOnlineAlert(false), 3000);
      }
    }
  }, [isOnline, showOfflineAlert, queueSize]);

  return (
    <>
      {/* Offline Alert with cached content info */}
      <Snackbar
        open={showOfflineAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: { xs: 8, md: 10 } }} // Account for header
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<WifiOff />}
          sx={{ minWidth: 300 }}
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {t('offline.title', 'You\'re offline')}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {cachedPagesCount > 0 ? (
                <>
                  <Storage fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 14 }} />
                  {cachedPagesCount} {t('offline.cachedItems', 'cached items available')}
                </>
              ) : (
                t('offline.limited', 'Some features may be limited')
              )}
            </Typography>
            {queueSize > 0 && (
              <Chip
                icon={<CloudQueue />}
                label={`${queueSize} ${t('offline.queuedRequests', 'requests queued')}`}
                size="small"
                color="warning"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Alert>
      </Snackbar>

      {/* Back Online Alert with sync status */}
      <Snackbar
        open={showOnlineAlert}
        autoHideDuration={isProcessingQueue ? null : 3000}
        onClose={() => setShowOnlineAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: { xs: 8, md: 10 } }}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={<Wifi />}
          sx={{ minWidth: 300 }}
        >
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {t('online.title', 'You\'re back online!')}
            </Typography>
            {isProcessingQueue && queueSize > 0 && (
              <>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  <CloudQueue fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 14 }} />
                  {t('online.syncing', 'Syncing')} {queueSize} {t('online.requests', 'requests...')}
                </Typography>
                <LinearProgress color="success" sx={{ mt: 1 }} />
              </>
            )}
            {!isProcessingQueue && queueSize === 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                <CloudDone fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 14 }} />
                {t('online.allSynced', 'All changes synced')}
              </Typography>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};

export default NetworkStatusIndicator;
